import { supabase } from '../lib/supabase'
import { REVIEW_QUEUE_STATUSES } from '../lib/jobStatusWorkflow'
import type { OperationalDivision } from '../lib/operationalDivisions'

export interface ReviewQueueJob {
  id: string
  title: string
  status: string
  actual_end_time?: string | null
  completion_notes?: string | null
  operational_division_id?: string | null
  service_type?: string | null
  job_number?: string | null
  customer?: {
    company_name?: string
    phone?: string | null
  } | null
  property?: {
    address?: string
    city?: string
  } | null
  techs: string[]
  photos: number
}

export interface ReviewQueueStats {
  pendingReview: number
  approved: number
  needsRevision: number
  total: number
}

export interface ReviewQueueResult {
  jobs: ReviewQueueJob[]
  stats: ReviewQueueStats
}

const REVIEW_JOB_SELECT =
  'id, title, status, actual_end_time, completion_notes, operational_division_id, service_type, job_number, customer:customers(company_name, phone), property:properties(address, city)'

type RpcReviewPayload = {
  jobs?: Array<{
    id: string
    title: string
    status: string
    actual_end_time?: string | null
    completion_notes?: string | null
    operational_division_id?: string | null
    service_type?: string | null
    job_number?: string | null
    customer?: { company_name?: string; phone?: string | null } | null
    property?: { address?: string; city?: string } | null
  }> | null
  assignments?: Array<{
    job_id: string
    first_name?: string | null
    last_name?: string | null
  }> | null
  photos?: Array<{ job_id: string; count?: number | null }> | null
  stats?: {
    pending_review?: number | null
    approved?: number | null
    needs_revision?: number | null
    total?: number | null
  } | null
}

function mapReviewEnrichment(payload: RpcReviewPayload) {
  const techsByJob = new Map<string, string[]>()
  for (const row of payload.assignments || []) {
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim()
    if (!name) continue
    const list = techsByJob.get(row.job_id) || []
    list.push(name)
    techsByJob.set(row.job_id, list)
  }

  const photosByJob = new Map<string, number>()
  for (const row of payload.photos || []) {
    photosByJob.set(row.job_id, Number(row.count || 0))
  }

  return { techsByJob, photosByJob }
}

function buildReviewJobs(
  rawJobs: NonNullable<RpcReviewPayload['jobs']>,
  enrichment: ReturnType<typeof mapReviewEnrichment>
): ReviewQueueJob[] {
  const { techsByJob, photosByJob } = enrichment
  return rawJobs.map((job) => ({
    id: job.id,
    title: job.title,
    status: job.status,
    actual_end_time: job.actual_end_time,
    completion_notes: job.completion_notes,
    operational_division_id: job.operational_division_id,
    service_type: job.service_type,
    job_number: job.job_number ?? null,
    customer: job.customer,
    property: job.property,
    techs: techsByJob.get(job.id) || [],
    photos: photosByJob.get(job.id) || 0,
  }))
}

function mapReviewStats(stats: RpcReviewPayload['stats']): ReviewQueueStats {
  return {
    pendingReview: Number(stats?.pending_review || 0),
    approved: Number(stats?.approved || 0),
    needsRevision: Number(stats?.needs_revision || 0),
    total: Number(stats?.total || 0),
  }
}

async function fetchViaRpc(division: OperationalDivision | null): Promise<ReviewQueueResult> {
  const { data, error } = await supabase.rpc('get_review_queue_jobs', {
    p_division_id: division?.id || null,
    p_service_types: division?.service_types?.length ? division.service_types : null,
    p_limit: 500,
  })
  if (error) throw error

  const payload = (Array.isArray(data) ? data[0] : data) as RpcReviewPayload | null
  if (!payload?.jobs) {
    throw Object.assign(new Error('empty review queue snapshot'), { code: 'PGRST116' })
  }

  const jobs = buildReviewJobs(payload.jobs, mapReviewEnrichment(payload))
  return { jobs, stats: mapReviewStats(payload.stats) }
}

async function fetchViaClientQueries(division: OperationalDivision | null): Promise<ReviewQueueResult> {
  const { data, error } = await supabase
    .from('jobs')
    .select(REVIEW_JOB_SELECT)
    .in('status', [...REVIEW_QUEUE_STATUSES])
    .order('actual_end_time', { ascending: false, nullsFirst: false })
    .limit(500)

  if (error) throw error

  const rawJobs = (data || []).filter(
    (job) => job.status !== 'completed' || !!job.actual_end_time
  )

  const jobs = await enrichReviewJobs(rawJobs)

  const stats: ReviewQueueStats = {
    pendingReview: jobs.filter(
      (j) => j.status === 'completed' || j.status === 'pending_review' || j.status === 'under_review'
    ).length,
    approved: 0,
    needsRevision: jobs.filter((j) => j.status === 'qc_failed').length,
    total: jobs.length,
  }

  if (!division) {
    const { count, error: approvedErr } = await supabase
      .from('jobs')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'approved')
    if (!approvedErr) stats.approved = count || 0
    return { jobs, stats }
  }

  const { data: approvedRows, error: approvedErr } = await supabase
    .from('jobs')
    .select('operational_division_id, service_type')
    .eq('status', 'approved')
  if (!approvedErr) {
    stats.approved = (approvedRows || []).filter((job) => {
      if (job.operational_division_id) return job.operational_division_id === division.id
      return !!job.service_type && (division.service_types || []).includes(job.service_type)
    }).length
  }

  return { jobs, stats }
}

async function enrichReviewJobs(jobs: any[]): Promise<ReviewQueueJob[]> {
  if (jobs.length === 0) return []

  const jobIds = jobs.map((job) => job.id)

  const [assignmentsRes, photosRes] = await Promise.all([
    supabase
      .from('job_assignments')
      .select('job_id, employee:employees(first_name, last_name)')
      .in('job_id', jobIds)
      .is('unassigned_at', null),
    supabase.from('job_photos').select('job_id').in('job_id', jobIds),
  ])

  const techsByJob = new Map<string, string[]>()
  for (const row of assignmentsRes.data || []) {
    const emp = (row as any).employee
    const name = emp ? `${emp.first_name} ${emp.last_name}`.trim() : null
    if (!name) continue
    const list = techsByJob.get(row.job_id) || []
    list.push(name)
    techsByJob.set(row.job_id, list)
  }

  const photosByJob = new Map<string, number>()
  for (const row of photosRes.data || []) {
    photosByJob.set(row.job_id, (photosByJob.get(row.job_id) || 0) + 1)
  }

  return jobs.map((job) => ({
    id: job.id,
    title: job.title,
    status: job.status,
    actual_end_time: job.actual_end_time,
    completion_notes: job.completion_notes,
    operational_division_id: job.operational_division_id,
    service_type: job.service_type,
    job_number: job.job_number ?? null,
    customer: job.customer,
    property: job.property,
    techs: techsByJob.get(job.id) || [],
    photos: photosByJob.get(job.id) || 0,
  }))
}

export async function fetchReviewQueue(
  division: OperationalDivision | null = null
): Promise<ReviewQueueResult> {
  return fetchViaRpc(division).catch(() => fetchViaClientQueries(division))
}

export async function fetchReviewQueueJobs(
  division: OperationalDivision | null = null
): Promise<ReviewQueueJob[]> {
  const { jobs } = await fetchReviewQueue(division)
  return jobs
}
